import { useState, useMemo } from "react";
import {
  Modal,
  Table,
  Form,
  Button,
  FormCheck,
  Badge,
  Alert,
} from "react-bootstrap";
import { useQuery } from "react-query";
import ReactPaginate from "react-paginate";
import Datetime from "react-datetime";
import currency from "currency.js";
import { toast } from "react-toastify";
import CachedIcon from "mdi-react/CachedIcon";
import { useStoreState } from "easy-peasy";
import queryString from "query-string";
import useDebounce, { useBackendUrl } from "../../utils/hooks";
import {
  formatForQty,
  paginationOptions,
  scrollToTop,
} from "../../utils/helpers";
import { format } from "date-fns";

export default function BatchSelectorModal({
  proceed,
  show,
  batches = [],
  quantityToHandle,
  previouslySelectedBatches = [],
  single = false,
  Bar_Code,
  loadBatchFromServer = false,
  itemsApiUrl = "/api/items",
  warehouse,
  withInput = true,
  stockCountDate,
  isDeduction = true,
  hasQuantity = true,
}) {
  const backendUrl = useBackendUrl();
  const generalSettings = useStoreState(state => state.generalSettings);

  // Object keyed by Bar_Code for O(1) lookups; converted to array on submit
  const [selectedBatches, setSelectedBatches] = useState(() =>
    (previouslySelectedBatches || []).reduce(
      (acc, b) => ({ ...acc, [b.Bar_Code]: b }),
      {}
    )
  );

  // Tracks user-typed "qty to handle" per batch; persists across page changes
  const [inputQties, setInputQties] = useState(() =>
    (previouslySelectedBatches || []).reduce(
      (acc, b) => ({ ...acc, [b.Bar_Code]: b.inputQty || "" }),
      {}
    )
  );

  const [showSelected, setShowSelected] = useState(false);

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    q: "",
    RegDate: /*stockCountDate || */ "",
    hasQuantity,
  });

  const debouncedQueryParams = useDebounce(queryParams, 500);

  const getBatchesForSales = async debouncedQueryParams => {
    const base = warehouse
      ? `${backendUrl}${itemsApiUrl}/batches-for-sales/${warehouse}/${Bar_Code}`
      : `${backendUrl}${itemsApiUrl}/batches-for-sales/${Bar_Code}`;
    let response = await fetch(
      `${base}?${queryString.stringify(debouncedQueryParams)}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();
    return data;
  };

  const { data = { batches: [], count: 0 }, isFetching, refetch } = useQuery(
    ["BATCH_SELECTOR", Bar_Code, warehouse, debouncedQueryParams],
    () => getBatchesForSales(debouncedQueryParams),
    {
      enabled: loadBatchFromServer && !!Bar_Code,
      keepPreviousData: true,
      onError: () => toast.error("Unable to load batches, Try again"),
    }
  );

  // Client-side filter when batches come from prop (not server)
  const filteredLocalBatches = useMemo(() => {
    if (loadBatchFromServer) return [];
    const q = queryParams.q.toLowerCase();
    if (!q) return batches;
    return batches.filter(
      b =>
        (b.Bar_Code || "").toLowerCase().includes(q) ||
        (b.Batch_Name || "").toLowerCase().includes(q) ||
        (b.ItemName || "").toLowerCase().includes(q)
    );
  }, [batches, queryParams.q, loadBatchFromServer]);

  const displayedBatches = loadBatchFromServer
    ? data.batches
    : filteredLocalBatches;

  // When withInput=true, total is the sum of typed quantities; otherwise full batch Quantity
  const totalSelected = useMemo(
    () =>
      withInput
        ? Object.keys(selectedBatches).reduce(
            (sum, barCode) => currency(sum).add(inputQties[barCode] || 0).value,
            0
          )
        : Object.values(selectedBatches).reduce(
            (sum, b) => currency(sum).add(b.Quantity).value,
            0
          ),
    [selectedBatches, inputQties, withInput]
  );

  const toggleBatch = batch => {
    if (single) {
      setSelectedBatches({ [batch.Bar_Code]: batch });
      return;
    }
    setSelectedBatches(prev => {
      if (prev[batch.Bar_Code]) {
        const { [batch.Bar_Code]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [batch.Bar_Code]: batch };
    });
  };

  // Typing a qty auto-selects the batch; clearing it deselects.
  // In deduction mode the typed qty cannot exceed the batch's available Quantity.
  const handleInputQty = (batch, value) => {
    setInputQties(prev => ({ ...prev, [batch.Bar_Code]: value }));
    if (value && Number(value) > 0) {
      setSelectedBatches(prev => ({ ...prev, [batch.Bar_Code]: batch }));
    } else {
      setSelectedBatches(prev => {
        const { [batch.Bar_Code]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = () => {
    if (Number(quantityToHandle) > Number(totalSelected)) {
      toast.error("Not Enough from selected Batches");
      return;
    }
    if (isDeduction) {
      const exceeded = Object.values(selectedBatches).find(
        b => Number(inputQties[b.Bar_Code]) > Number(b.Quantity)
      );
      if (exceeded) {
        toast.error(
          `Qty for batch ${
            exceeded.Bar_Code
          } cannot exceed available quantity (${currency(exceeded.Quantity, {
            symbol: "",
            precision: 2,
            format: formatForQty,
          }).format()})`
        );
        return;
      }
    }
    proceed({
      batches: Object.values(selectedBatches).map(b => ({
        ...b,
        inputQty: withInput ? inputQties[b.Bar_Code] || 0 : b.Quantity,
        isDeduction,
      })),
    });
  };

  const selectedCount = Object.keys(selectedBatches).length;
  const tableBatches = showSelected
    ? Object.values(selectedBatches)
    : displayedBatches;
  const colSpan = withInput ? 8 : 7;

  return (
    <Modal
      show={show}
      onHide={() => proceed({ batches: [] })}
      centered
      animation={false}
      enforceFocus={false}
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3 w-100 me-3">
          <h1 className="h6 mb-0">
            Select Batch{" "}
            {isDeduction ? (
              <Badge bg="danger" className="ms-1 fw-normal fs-8">
                Deduction
              </Badge>
            ) : (
              <Badge bg="primary" className="ms-1 fw-normal fs-8">
                Addition
              </Badge>
            )}
          </h1>
          {loadBatchFromServer && Bar_Code && !showSelected && (
            <Button variant="" onClick={() => refetch()}>
              <CachedIcon />
            </Button>
          )}
          <Button
            variant={showSelected ? "primary" : "outline-primary"}
            size="sm"
            className="ms-auto"
            onClick={() => setShowSelected(s => !s)}
          >
            {showSelected
              ? "Show All Batches"
              : `View Selected (${selectedCount})`}
          </Button>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!showSelected && (
          <div className="row g-2 mb-3">
            <div className="col">
              <Form.Control
                placeholder="Search batch or item…"
                value={queryParams.q}
                onChange={e =>
                  setQueryParams(p => ({ ...p, page: 1, q: e.target.value }))
                }
              />
            </div>
            {loadBatchFromServer && (
              <div className="col-auto d-flex gap-2 align-items-center">
                <Datetime
                  dateFormat="MMM DD, YYYY"
                  timeFormat={false}
                  closeOnSelect
                  inputProps={{
                    className: "date-input form-control",
                    placeholder: "Filter by date",
                  }}
                  value={queryParams.RegDate}
                  onChange={date =>
                    setQueryParams(p => ({ ...p, page: 1, RegDate: date }))
                  }
                />
                {queryParams.RegDate && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setQueryParams(p => ({ ...p, RegDate: "" }))}
                  >
                    ✕
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {isDeduction ? (
          <Alert variant="warning" className="py-2 px-3 mb-3 small">
            Deduction mode: each batch qty entered cannot exceed its available
            quantity in stock.
          </Alert>
        ) : (
          <Alert variant="primary" className="py-2 px-3 mb-3 small">
            Addition mode: enter batch qty to add to.
          </Alert>
        )}

        <div className="d-flex gap-4 align-items-center mb-3">
          <p className="my-0">
            <b>Quantity Required to {isDeduction ? "Deduct" : "Add"}:</b>{" "}
            {currency(quantityToHandle, {
              symbol: "",
              precision: 2,
              format: formatForQty,
            }).format()}
          </p>

          <p className="my-0">
            <b>Total Quantity in selected Batches:</b>{" "}
            {currency(totalSelected, {
              symbol: "",
              precision: 2,
              format: formatForQty,
            }).format()}
          </p>
        </div>

        <Table borderless hover striped className="product-table">
          <thead className="sticky text-nowrap">
            <tr>
              <th />
              <th scope="col">Batch</th>
              {/* <th scope="col">{generalSettings?.Batch_Name}</th> */}
              <th>Vendor Invoice No.</th>
              <th scope="col">Item Name</th>
              <th scope="col">Unit Cost</th>
              <th scope="col">Qty Now</th>
              {withInput && (
                <th scope="col">Qty to {isDeduction ? "Deduct" : "Add"}</th>
              )}
              <th scope="col">Date</th>
            </tr>
          </thead>
          <tbody>
            {isFetching ? (
              <tr>
                <td colSpan={colSpan} className="text-center py-3">
                  Loading…
                </td>
              </tr>
            ) : tableBatches.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="text-center py-3">
                  {showSelected
                    ? "No batches selected yet"
                    : "No batches found"}
                </td>
              </tr>
            ) : (
              tableBatches.map((el, index) => (
                <tr
                  className="p-cursor"
                  key={index}
                  onClick={() => toggleBatch(el)}
                >
                  <td>
                    <FormCheck
                      checked={!!selectedBatches[el.Bar_Code]}
                      onChange={() => {}}
                    />
                  </td>
                  <td>
                    {el.Bar_Code ? el.Bar_Code.split("-").pop() : "..."}{" "}
                    <i>{el.Batch_Name || ""}</i>
                  </td>

                  <td>
                    {el?.InvoiceNo ? (
                      <a
                        to={`/reports/purchase-history?NoDate=${true}&invoiceNumber=${
                          el?.InvoiceNo
                        }`}
                        className="fw-5"
                        target="_blank"
                      >
                        {el?.InvoiceNo}
                      </a>
                    ) : (
                      "..."
                    )}
                    {/* {el?.PurchaseID && (
                      <small>
                        <br />
                        {el.PurchaseID}
                      </small>
                    )} */}
                  </td>
                  <td title={el?.Quantity}>{el.ItemName}</td>
                  <td>{currency(el?.UnitCost, { symbol: "" }).format()}</td>
                  <td>
                    {currency(el?.Quantity, {
                      precision: 2,
                      format: formatForQty,
                      symbol: "",
                    }).format()}
                  </td>
                  {withInput && (
                    <td onClick={e => e.stopPropagation()}>
                      <Form.Control
                        type="number"
                        min={0}
                        max={el.Quantity}
                        placeholder="0"
                        style={{ width: "100px" }}
                        value={inputQties[el.Bar_Code] || ""}
                        onChange={e => handleInputQty(el, e.target.value)}
                      />
                    </td>
                  )}
                  <td className="text-nowrap">
                    {el.RegDate
                      ? format(new Date(el.RegDate), "dd MMM, yyyy")
                      : "..."}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {!showSelected &&
          loadBatchFromServer &&
          data.count > queryParams.limit && (
            <ReactPaginate
              {...paginationOptions}
              pageCount={Math.ceil(data.count / queryParams.limit)}
              forcePage={queryParams.page - 1}
              onPageChange={({ selected }) => {
                scrollToTop();
                setQueryParams(p => ({ ...p, page: selected + 1 }));
              }}
            />
          )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between mt-3">
          <div />
          <Button
            disabled={isFetching}
            variant="primary"
            className="p-2 px-4"
            onClick={handleSubmit}
          >
            {isFetching ? "Please wait…" : "Continue"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
